import { DashboardLayouts, LocalStorageLayoutData } from './dashboardLayoutTypes';
import { STORAGE_KEYS } from './dashboardLayoutConstants';
import { isValidLayout } from './dashboardLayoutUtils';
import { log } from '@/shared/utils/logger';

/* ============================================================================
 * DASHBOARD LAYOUT LOCAL STORAGE OPERATIONS
 * Handles all localStorage operations for dashboard layouts
 * ============================================================================ */

/**
 * Save layouts to localStorage
 */
export function saveToLocalStorage(viewMode: string, layouts: DashboardLayouts): void {
  const key = STORAGE_KEYS.LAYOUT(viewMode);
  const timestamp = new Date().toISOString();
  
  try {
    const storageData: LocalStorageLayoutData = {
      layouts,
      lastModified: timestamp,
      syncStatus: 'pending'
    };
    
    localStorage.setItem(key, JSON.stringify(storageData));
    // log.debug('DashboardLayoutStorage', LOG_MESSAGES.SAVE_SUCCESS(key));
  } catch (error) {
    log.error('DashboardLayoutStorage', 'Failed to save layout to localStorage:', error);
  }
}

/**
 * Load layouts from localStorage
 */
export function loadFromLocalStorage(viewMode: string): LocalStorageLayoutData | null {
  const key = STORAGE_KEYS.LAYOUT(viewMode);
  
  try {
    const data = localStorage.getItem(key);
    if (!data) return null;
    
    const parsed = JSON.parse(data);
    if (isValidLayout(parsed.layouts)) {
      return parsed;
    }
  } catch (error) {
    log.error('DashboardLayoutStorage', 'Failed to load layout from localStorage:', error);
  }
  
  return null;
}

/**
 * Mark localStorage layout as synced
 */
export function markLocalStorageAsSynced(viewMode: string): void {
  const key = STORAGE_KEYS.LAYOUT(viewMode);
  const data = loadFromLocalStorage(viewMode);
  
  if (data) {
    data.syncStatus = 'synced';
    try {
      localStorage.setItem(key, JSON.stringify(data));
      // log.debug('DashboardLayoutStorage', LOG_MESSAGES.SYNC_SUCCESS(key));
    } catch (error) {
      log.error('DashboardLayoutStorage', 'Failed to update sync status:', error);
    }
  }
}

/**
 * Mark localStorage layout as pending sync
 */
export function markLocalStorageAsPending(viewMode: string): void {
  const key = STORAGE_KEYS.LAYOUT(viewMode);
  const data = loadFromLocalStorage(viewMode);
  
  if (data) {
    data.syncStatus = 'pending';
    try {
      localStorage.setItem(key, JSON.stringify(data));
      // log.debug('DashboardLayoutStorage', LOG_MESSAGES.SYNC_PENDING(key));
    } catch (error) {
      log.error('DashboardLayoutStorage', 'Failed to update sync status:', error);
    }
  }
}

/**
 * Check if a layout is synced locally
 */
export function isLayoutSyncedLocally(viewMode: string, layoutId: string): boolean {
  const currentLayoutKey = STORAGE_KEYS.CURRENT_LAYOUT(viewMode);
  const currentLayout = localStorage.getItem(currentLayoutKey);
  
  if (currentLayout) {
    try {
      const parsed = JSON.parse(currentLayout);
      return parsed.id === layoutId;
    } catch (error) {
      log.error('DashboardLayoutStorage', 'Failed to parse current layout:', error);
    }
  }

  return false;
}

/**
 * Clear persisted unnamed/current layout for a view mode
 */
export function clearLocalStorageLayout(viewMode: string): void {
  localStorage.removeItem(STORAGE_KEYS.LAYOUT(viewMode));
}
